import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Eyebrow, Marker } from "./Bits";
import Reveal from "@/components/Reveal";
import useIsPhone from "@/hooks/useIsPhone";

/* Free Resources + FAQ — Figma node 102:3193 (Frame 157), 1361 wide.

   Deliberately NOT a scaled stage: the accordion changes height when an answer
   opens, which a fixed-height stage would clip. Type steps up at lg/xl instead so
   it keeps pace with the stage-based sections either side of it.

   The shadcn/Radix accordion ships an h-4 w-4 chevron; it is overridden below to
   the design's 32px. */

const RESOURCES = [
  {
    badge: "Free Guide", badgeBg: "#A21C93", badgeInk: "#fff", bg: "#F7CFE3", ink: "#8E1071",
    title: "Creator Starter Kit",
    desc: "Everything you need to kickstart your creator journey.",
    cta: "Download Now →",
  },
  {
    badge: "Career Roadmap", badgeBg: "#fff", badgeInk: "#0B5C2E", bg: "#A5E6C4", ink: "#0B5C2E",
    title: "Your Learning Roadmap",
    desc: "Discover the right path based on your goals.",
    cta: "View Roadmap →",
  },
  {
    badge: "Skill Assessment", badgeBg: "#4A4508", badgeInk: "#fff", bg: "#FAF07F", ink: "#6B6410",
    title: "Skill Assessment",
    desc: "Identify your strengths and find the right learning path.",
    cta: "Take Assessment →",
  },
  {
    badge: "Career Planner", badgeBg: "#12305F", badgeInk: "#fff", bg: "#A9EDE6", ink: "#12457F",
    title: "Career Planner",
    desc: "Plan your learning journey and track your growth.",
    cta: "Get Started →",
  },
];

const FAQS = [
  {
    q: "How do I get started?",
    a: "Create a free account, browse the skill paths, and start with any beginner course. You can upgrade to a package whenever you are ready.",
  },
  {
    q: "Are the courses beginner-friendly?",
    a: "Yes. Our programs are designed for learners at all levels, with structured content that helps you progress from fundamentals to advanced concepts.",
  },
  {
    q: "Can I learn at my own pace?",
    a: "Every course is self-paced. Your progress is saved automatically, so you can pick up exactly where you left off on any device.",
  },
  {
    q: "How can I become a creator or mentor?",
    a: "Apply through the partner program. Once approved you can publish courses, mentor learners, and earn from the GENRYZ ecosystem.",
  },
];

export default function ResourcesFaqSection() {
  const isPhone = useIsPhone();

  return (
    <section id="resources" className="mx-auto w-full max-w-[1840px] px-5 py-12 sm:px-6 lg:px-[2.3%] lg:py-8 xl:py-10">

      {/* Stacked, the eyebrow leads the heading; at lg the row order is restored.
          Below sm the copy centres — from sm up the original left alignment. */}
      <div className="flex flex-col-reverse items-center gap-5 text-center sm:items-start sm:text-left lg:flex-row lg:items-start lg:justify-between lg:gap-6">
        <Reveal className="min-w-0">
          <h2 className="mx-auto max-w-[960px] font-poppins text-[30px] font-medium leading-[1.65] text-black sm:mx-0 sm:text-[38px] lg:text-[42px] xl:text-[56px]">
            Free<Marker>Resources</Marker>to Get You Started
          </h2>
          <p className="mx-auto mt-4 max-w-[700px] font-poppins text-[16px] font-light leading-[1.5] text-black sm:mx-0 lg:text-[17px] xl:text-[21px]">
            Explore free tools, guides, and answers to help you begin your learning journey with confidence.
          </p>
        </Reveal>
        <Reveal delay={80} className="shrink-0 lg:pt-8">
          <Eyebrow height={60} className="min-w-[135px]">FAQs</Eyebrow>
        </Reveal>
      </div>

      {/* Design splits 702:571 with an 88 gutter, not 1:1. */}
      <div className="mt-12 grid gap-10 lg:grid-cols-[1.23fr_1fr] lg:gap-16 xl:mt-16 xl:gap-[88px]">

        {/* Below sm a swipe rail rather than a stack, so the four cards cost one
            screen; it bleeds to the viewport edge so the next card peeks. From sm
            up it is the original grid.

            overflow-y-hidden is a guard, not cosmetic: with overflow-x auto CSS
            computes a visible overflow-y to auto, so anything overhanging the
            rail vertically would turn it into a vertical scroller. */}
        <div className="hide-scrollbar -mx-5 ml-1 flex snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-hidden px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-6 sm:overflow-visible sm:px-0 sm:pb-0">
          {RESOURCES.map((r, i) => (
            <Reveal
              key={r.title}
              delay={i * 70}
              still={isPhone}
              className="h-full w-[80vw] max-w-[320px] shrink-0 snap-start sm:w-auto sm:max-w-none sm:shrink"
            >
              <div className="flex h-full flex-col rounded-[20px] p-6 lg:rounded-[16px] lg:p-7 xl:p-[38px]" style={{ background: r.bg }}>
                <span
                  className="inline-flex h-[33px] w-fit items-center justify-center rounded-[45px] px-4 font-poppins text-[13px] font-medium xl:h-[42px] xl:px-5 xl:text-[17px]"
                  style={{ background: r.badgeBg, color: r.badgeInk }}
                >
                  {r.badge}
                </span>
                <h3 className="mt-4 font-montserrat text-[21px] font-semibold text-black xl:mt-5 xl:text-[28px]">
                  {r.title}
                </h3>
                <p className="mt-1.5 font-poppins text-[15px] leading-[1.4] text-black/80 xl:text-[19px]">
                  {r.desc}
                </p>
                <button
                  onClick={() => document.getElementById("skill-paths")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-5 inline-flex w-fit items-center rounded-full bg-white/70 px-4 py-2 font-poppins text-[15px] font-medium transition-opacity hover:opacity-70 lg:rounded-none lg:bg-transparent lg:px-0 lg:py-0 xl:mt-6 xl:text-[19px]"
                  style={{ color: r.ink }}
                >
                  {r.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120} className="min-w-0">
          <div className="rounded-[20px] bg-[var(--fig-surface)] px-5 lg:rounded-none lg:bg-transparent lg:px-0">
            <Accordion type="single" collapsible defaultValue="faq-1" className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  className="border-b-[1.5px] border-black/15 last:border-b-0 lg:border-black lg:last:border-b-[1.5px]"
                >
                  <AccordionTrigger className="gap-5 py-5 text-left font-poppins text-[17px] font-normal text-black hover:no-underline [&>svg]:h-6 [&>svg]:w-6 [&>svg]:text-black lg:gap-6 lg:text-[19px] lg:[&>svg]:h-7 lg:[&>svg]:w-7 xl:py-7 xl:text-[30px] xl:[&>svg]:h-9 xl:[&>svg]:w-9">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-4 font-poppins text-[15px] font-light leading-[1.6] text-[var(--fig-body)] lg:pr-8 lg:text-[16px] lg:leading-[1.55] lg:text-black xl:pb-8 xl:text-[21px]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
